import { View } from 'react-native'
import { useThrottledCallback } from 'use-debounce'
import Typography from '@ui/typography'
import {
  sContainer,
  sPercentageLabel,
  sSliderTrack,
  sliderTrackContainerStyle,
} from './styles'
import { NumberUtils } from '@ca/common'
import cn from '@ui/utils/cn'
import { colors } from '@ui/colors'
import SliderSideButton from './SliderSideButton'
import SliderTrack from './SliderTrack'
import type { SliderVariant } from './types'
import { resolveSteppedValueStep } from './steppedTrackUtils'
import {
  DEFAULT_MAX_SNAP_PERCENT,
  getDefaultTrackStep,
  resolveDefaultValue,
} from './defaultTrackUtils'

interface Props {
  min: number
  max: number
  value: number
  step: number
  variant?: SliderVariant
  steps?: number
  minPercentage?: number
  maxPercentage?: number
  buttonStart?: string
  buttonEnd?: string
  showPercentageLabel?: boolean
  /** Green track at max. Defaults to `showPercentageLabel` for existing callers. */
  highlightAtMax?: boolean
  /** Top-of-track zone that snaps to max, as % of track width. */
  maxSnapPercent?: number
  onChange: (value: number) => void
  disabled?: boolean
  hasError?: boolean
}

const getDisplayValue = (value: number, max: number, min: number): number => {
  if (value > max) {
    return max
  }
  if (value < min) {
    return min
  }
  return value
}

const getSliderValue = (
  displayValue: number,
  min: number,
  max: number,
  minPercentage: number,
  maxPercentage: number
): number => {
  if (max === min) {
    return minPercentage
  }

  const ratio = (displayValue - min) / (max - min)
  return minPercentage + ratio * (maxPercentage - minPercentage)
}

const getValueFromSlider = (
  sliderValue: number,
  min: number,
  max: number,
  minPercentage: number,
  maxPercentage: number
): number => {
  if (maxPercentage === minPercentage) {
    return min
  }

  const ratio = (sliderValue - minPercentage) / (maxPercentage - minPercentage)
  return min + ratio * (max - min)
}

const getSteppedTrackStep = (
  min: number,
  max: number,
  minPercentage: number,
  maxPercentage: number,
  step: number,
  steps?: number
): number => {
  const valueStep = resolveSteppedValueStep({
    minimumValue: min,
    maximumValue: max,
    step,
    steps,
  })

  if (max === min) {
    return valueStep
  }

  const ratio = (maxPercentage - minPercentage) / (max - min)
  return valueStep * ratio
}

// Snap a continuous value back onto the step grid so converting from the
// slider position doesn't accumulate floating-point drift.
const snapValueToStep = (
  rawValue: number,
  min: number,
  max: number,
  step: number,
  steps?: number
): number => {
  const valueStep = resolveSteppedValueStep({
    minimumValue: min,
    maximumValue: max,
    step,
    steps,
  })

  if (valueStep <= 0) {
    return rawValue
  }

  const snapped = min + Math.round((rawValue - min) / valueStep) * valueStep
  return Math.min(max, Math.max(min, snapped))
}

export default function Slider({
  max,
  min,
  value,
  step,
  variant = 'default',
  steps,
  minPercentage = 0,
  maxPercentage = 100,
  buttonStart,
  buttonEnd,
  showPercentageLabel = true,
  highlightAtMax = showPercentageLabel,
  maxSnapPercent = DEFAULT_MAX_SNAP_PERCENT,
  disabled,
  onChange,
  hasError = false,
}: Props) {
  const displayValue = getDisplayValue(value, max, min)
  const percentage = NumberUtils.percentage.getSafe(displayValue, max)
  const trackValue = getSliderValue(
    displayValue,
    min,
    max,
    minPercentage,
    maxPercentage
  )

  const throttledOnChange = useThrottledCallback(onChange, 150)

  const handleSliderChange = (nextSliderValue: number) => {
    if (disabled) {
      return false
    }

    if (variant === 'stepped') {
      const rawValue = getValueFromSlider(
        nextSliderValue,
        min,
        max,
        minPercentage,
        maxPercentage
      )
      throttledOnChange(snapValueToStep(rawValue, min, max, step, steps))
      return
    }

    throttledOnChange(
      resolveDefaultValue(
        nextSliderValue,
        min,
        max,
        step,
        minPercentage,
        maxPercentage,
        maxSnapPercent
      )
    )
  }

  const isMax = max > 0 && displayValue >= max
  const isOutsideMax = max > 0 && value > max
  const isOutsideMin = min > 0 && value < min
  const errored = hasError || isOutsideMax || isOutsideMin
  const isFull = isMax && highlightAtMax && !errored && !disabled

  const colorTrackFront = (() => {
    if (disabled) return colors.neutral[300]
    if (errored) return colors.red[600]
    if (isFull) return colors.green[600]
    return colors.text
  })()

  const colorTrackBack = (() => {
    if (disabled) return colors.black10
    if (errored) return colors.red25
    return colors.black10
  })()

  const handleMinPress = () => {
    if (disabled) return
    onChange(min)
  }

  const handleMaxPress = () => {
    if (disabled) return
    onChange(max)
  }

  const trackStep =
    variant === 'stepped'
      ? getSteppedTrackStep(min, max, minPercentage, maxPercentage, step, steps)
      : getDefaultTrackStep(min, max, step, minPercentage, maxPercentage)

  return (
    <View
      className={cn(sContainer, disabled && 'cursor-not-allowed opacity-100')}
    >
      {buttonStart && (
        <SliderSideButton
          label={buttonStart}
          onPress={handleMinPress}
          disabled={disabled}
          active={value === min}
        />
      )}
      <View
        className={cn(sSliderTrack, !disabled && 'cursor-pointer')}
        style={sliderTrackContainerStyle}
      >
        <SliderTrack
          variant={variant}
          minimumValue={minPercentage}
          maximumValue={maxPercentage}
          value={trackValue}
          step={trackStep}
          steps={steps}
          disabled={disabled}
          onValueChange={handleSliderChange}
          colorTrackFront={colorTrackFront}
          colorTrackBack={colorTrackBack}
        />
        {disabled && (
          <View className="pointer-events-auto absolute inset-0 opacity-0" />
        )}
      </View>
      {showPercentageLabel && (
        <Typography
          variant="SMB"
          className={cn(sPercentageLabel)}
          style={{ color: colorTrackFront }}
        >
          {!!isOutsideMin && '<'}
          {!!isOutsideMax && '>'}
          {Math.round(percentage)}%
        </Typography>
      )}
      {buttonEnd && (
        <SliderSideButton
          label={buttonEnd}
          onPress={handleMaxPress}
          disabled={disabled}
          active={value === max}
          colorActive={colors.green[600]}
        />
      )}
    </View>
  )
}
